import React from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, TextField } from '@material-ui/core';
import type { FormField } from '../interfaces';
import { getLocalizedLabel } from '../utils';
import { ActionButton } from '@klp/design';
import { string } from 'yup';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

export const LinkSelector = ({
    isRequired,
    isReadOnly,
    field,
}: {
    isRequired?: boolean;
    isReadOnly?: boolean;
    field?: FormField;
}) => {
    const { t } = useTranslation();
    const classes = getFormFieldStyles();

    const yupUrlValidator = string().url();
    const checkValidLink = (value) => {
        if (!value || value === '') {
            return;
        }

        if (!yupUrlValidator.isValidSync(value)) {
            return t('project.errors.invalidLink');
        }

        return;
    };

    const [linkFormikField, linkFormikMeta] = useField({
        name: field?.id as string,
        validate: checkValidLink,
    });

    return (
        <>
            {isReadOnly ? (
                <>
                    {linkFormikField?.value && (
                        <Box className={classes.container}>
                            <ActionButton
                                actionType="external"
                                title={linkFormikField?.value}
                                translation={getLocalizedLabel(
                                    `project.formFields.${field?.id}`,
                                    field?.label,
                                    field?.id,
                                )}
                                onClick={() =>
                                    window.open(
                                        linkFormikField?.value,
                                        '_blank',
                                    )
                                }
                            />
                        </Box>
                    )}
                </>
            ) : (
                <Box className={classes.container}>
                    <TextField
                        variant="outlined"
                        fullWidth={true}
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        {...linkFormikField}
                        required={isRequired}
                        disabled={isReadOnly}
                        label={getLocalizedLabel(
                            `project.formFields.${field?.id}`,
                            field?.label,
                            field?.id,
                        )}
                        inputProps={{
                            'aria-readonly': !!isReadOnly,
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        helperText={
                            linkFormikMeta.touched ? linkFormikMeta.error : ''
                        }
                        error={
                            linkFormikMeta.touched &&
                            Boolean(linkFormikMeta.error)
                        }
                    />
                </Box>
            )}
        </>
    );
};
